/**
 * «Aprende Dashbo» sin React: lo que la página, el lanzador del riel y la entrada del menú leen de
 * `GET learning/me/` (contrato §2 de la tanda 2026-10-03-aprende-dashbo).
 *
 * El backend decide QUÉ tareas aplican, en qué pista y en qué orden; acá sólo se les pone cara. Una
 * clave que este código no conoce no se dibuja (mismo contrato que el drawer «Tus primeros pasos»):
 * una fila con la clave cruda es peor que una tarea de menos, y así el backend puede sumar una tarea
 * sin desplegar el frontend. Por eso los conteos de cada pista se recalculan sobre las tareas que se
 * dibujan: si no, una pista diría «5 de 6» con cinco filas a la vista.
 *
 * Vive fuera de los componentes porque React Router 7 no carga en Jest: la lógica que importa se
 * testea acá, sin montar nada.
 */

export const TRACK_ESSENTIALS = 'essentials';
export const TRACK_MEDIA_BUYING = 'media_buying';
export const TRACK_CLIENT_SERVICE = 'client_service';
export const TRACK_LEADERSHIP = 'leadership';
export const TRACK_ADVANCED = 'advanced';

export const KNOWN_TRACKS = [TRACK_ESSENTIALS, TRACK_MEDIA_BUYING, TRACK_CLIENT_SERVICE, TRACK_LEADERSHIP,
    TRACK_ADVANCED];

/** Los perfiles que se eligen con los chips: cada uno es la pista del mismo nombre. */
export const PROFILE_ROLES = [TRACK_MEDIA_BUYING, TRACK_CLIENT_SERVICE, TRACK_LEADERSHIP];

export const TASK_JOINED = 'joined_agency';
export const TASK_FIRST_VISIT = 'first_visit_tour';
export const TASK_ASSISTANT = 'assistant_connected';
export const TASK_FIRST_QUERY = 'first_client_query';
export const TASK_ALERTS = 'alerts_configured';

// Los textos de la tarea de alertas cuando la agencia tiene el Centro de Alertas (C-08 del recorrido
// con Pablo; D1 de la tanda del 26/10): la tarea es otra («Elige qué alertas te llegan», en «Mis reglas»), y
// la clave del backend sigue siendo la misma.
const ALERTS_CENTER_COPY = 'alerts_configured_center';

// Las 22 de `catalogo.md`. El orden no sale de acá: lo manda el payload.
export const KNOWN_TASKS = [
    TASK_JOINED, TASK_FIRST_VISIT, 'budget_or_goal_adjusted', TASK_ASSISTANT, TASK_FIRST_QUERY, 'report_saved',
    'line_created', 'conversions_chosen', 'daily_limit_set', 'alerts_configured', 'columns_configured',
    'memory_added', 'portal_invited', 'report_shared_portal', 'agency_prompt_used',
    'team_invited', 'clients_assigned', 'team_progress_reviewed', 'agency_prompt_created',
    'media_plan_synced', 'custom_metric_created', 'source_linked',
];

export const STATE_PENDING = 'pending';
export const STATE_DONE = 'done';
export const STATE_SKIPPED = 'skipped';
const STATES = [STATE_PENDING, STATE_DONE, STATE_SKIPPED];

export const SOURCE_MANUAL = 'manual';

/** Lo que pasa FUERA de Dashbo (en ChatGPT o Claude): sólo se ve al volver a preguntar. */
export const OUTSIDE_TASKS = [TASK_ASSISTANT, TASK_FIRST_QUERY];

/** Cuántos días se muestra el lanzador con «Lo esencial, completo» antes de retirarse. */
export const LAUNCHER_DONE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const KNOWN_TASK_SET = new Set(KNOWN_TASKS);
const KNOWN_TRACK_SET = new Set(KNOWN_TRACKS);

function normalizeMedia(raw) {
    if (!raw || (raw.kind !== 'animation' && raw.kind !== 'video')) return null;
    // Un video sin URL no tiene nada que embeber. Una animación sin URL sí vale: es una de las del
    // bundle, que se busca por la clave de la tarea (contrato §4).
    if (raw.kind === 'video' && !raw.url) return null;
    return {
        kind: raw.kind,
        url: raw.url || null,
        posterUrl: raw.poster_url || null,
        seconds: Number.isFinite(raw.seconds) ? raw.seconds : null,
    };
}

function normalizeTask(raw) {
    const state = STATES.includes(raw.state) ? raw.state : STATE_PENDING;
    // «Lo hizo …» vale aunque la otra persona no tenga nombre cargado (`User._full_name` puede venir
    // vacío): sin esto, la tarea de la agencia se leía como hecha por quien mira, y se le celebraba.
    const doneBy = raw.done_by ? {id: raw.done_by.id, name: raw.done_by.name || null} : null;
    return {
        key: raw.key,
        state,
        doneAt: state === STATE_DONE ? (raw.done_at || null) : null,
        doneBy: state === STATE_DONE ? doneBy : null,
        source: raw.source || null,
        canMark: raw.can_mark === true,
        // «Omitir» existe en todas salvo la que viene hecha (catálogo, regla 8).
        canSkip: raw.key !== TASK_JOINED,
        minutes: Number.isFinite(raw.minutes) ? raw.minutes : 0,
        clientId: raw.client_id === null || raw.client_id === undefined ? null : raw.client_id,
        guideSlug: raw.guide_slug || null,
        // Los recorridos que le FALTAN a la persona, en orden (sólo `first_visit_tour`, contrato §4 de
        // la tanda del 14/10). `null` = el backend no lo manda (viejo): se lanza `guideSlug`, como antes.
        guideSlugs: Array.isArray(raw.guide_slugs) ? raw.guide_slugs.filter((slug) => typeof slug === 'string' && slug) : null,
        // La agencia tiene el Centro de Alertas (sólo `alerts_configured`, contrato §3). Sin la clave es
        // `false`: con el backend viejo crear una regla del Centro no tilda la tarea, así que mandar ahí
        // prometería algo que no pasa.
        alertsCenter: raw.alerts_center === true,
        articleUrl: raw.article_url || null,
        media: normalizeMedia(raw.media),
    };
}

/**
 * De qué claves de `learn_dashbo.tasks.*` salen los textos de la tarea: los de su clave, salvo la de
 * alertas con el Centro, que es otra tarea con la misma clave del backend.
 */
export function taskCopyKey(task) {
    if (task && task.key === TASK_ALERTS && task.alertsCenter) return ALERTS_CENTER_COPY;
    return task ? task.key : '';
}

/** Lo mismo desde la clave, para los textos que sólo tienen la clave y el modelo (el lanzador, el aviso). */
export function copyKeyOf(model, key) {
    const task = taskOf(model, key);
    return task ? taskCopyKey(task) : key;
}

/**
 * Los números de una pista, siempre sobre las tareas que se dibujan. «Faltan» son las que no están
 * hechas, omitidas incluidas: omitir no cuenta como hecha (D5), y los minutos son los de esas mismas.
 */
export function withTrackCounts(track) {
    const tasks = track.tasks;
    const notDone = tasks.filter((task) => task.state !== STATE_DONE);
    return {
        ...track,
        done: tasks.length - notDone.length,
        total: tasks.length,
        skipped: tasks.filter((task) => task.state === STATE_SKIPPED).length,
        minutesLeft: notDone.reduce((sum, task) => sum + task.minutes, 0),
    };
}

function normalizeTrack(raw) {
    const tasks = (raw.tasks || []).filter((task) => task && KNOWN_TASK_SET.has(task.key)).map(normalizeTask);
    return withTrackCounts({key: raw.key, tasks, achieved: raw.achieved === true});
}

function findTask(tracks, key) {
    for (const track of tracks) {
        const task = track.tasks.find((each) => each.key === key);
        if (task) return {task, track};
    }
    return null;
}

/**
 * La próxima tarea: la que manda el backend si este código la dibuja y sigue pendiente; si no, la
 * primera pendiente en el orden de las pistas (lo esencial primero, después las del perfil).
 */
export function nextTaskOf(tracks, preferred) {
    const found = preferred ? findTask(tracks, preferred) : null;
    if (found && found.task.state === STATE_PENDING) return preferred;
    for (const track of tracks) {
        const task = track.tasks.find((each) => each.state === STATE_PENDING);
        if (task) return task.key;
    }
    return null;
}

/** El payload de `learning/me/` normalizado. `data` es el `data` del `OKResponse`. */
export function normalizeLearning(data) {
    const raw = data || {};
    const tracks = (raw.tracks || [])
        .filter((track) => track && KNOWN_TRACK_SET.has(track.key))
        .map(normalizeTrack)
        .filter((track) => track.tasks.length > 0);
    const nudgeTask = raw.nudge && findTask(tracks, raw.nudge.task_key);
    return {
        enabled: raw.enabled === true,
        agencyName: raw.agency_name || '',
        profile: {
            roles: ((raw.profile && raw.profile.roles) || []).filter((role) => PROFILE_ROLES.includes(role)),
            chosen: Boolean(raw.profile && raw.profile.chosen === true),
        },
        isAdmin: raw.is_admin === true,
        tracks,
        nextTask: nextTaskOf(tracks, raw.next_task),
        // Un aviso sobre una tarea que no se dibuja no tiene qué nombrar: se calla.
        nudge: raw.nudge && raw.nudge.from_name && nudgeTask ? {
            fromName: raw.nudge.from_name,
            taskKey: raw.nudge.task_key,
            createdAt: raw.nudge.created_at || null,
        } : null,
    };
}

export function taskOf(model, key) {
    const found = model ? findTask(model.tracks, key) : null;
    return found ? found.task : null;
}

export function trackOfTask(model, key) {
    const found = model ? findTask(model.tracks, key) : null;
    return found ? found.track : null;
}

export function essentialsOf(model) {
    return (model && model.tracks.find((track) => track.key === TRACK_ESSENTIALS)) || null;
}

/**
 * El mismo modelo con una tarea en otro estado, para pintarlo mientras el POST viaja (mismo motivo
 * que `withStepDone` del drawer: un tilde que tarda medio segundo se lee como un clic que no entró).
 * Recalcula los números con la misma regla que `normalizeLearning`; la verdad sigue siendo la del
 * backend, y esto se descarta en cuanto contesta.
 */
export function withTaskState(model, key, state, {now = new Date()} = {}) {
    if (!model) return model;
    const tracks = model.tracks.map((track) => {
        if (!track.tasks.some((task) => task.key === key)) return track;
        const tasks = track.tasks.map((task) => {
            if (task.key !== key) return task;
            const done = state === STATE_DONE;
            return {
                ...task,
                state,
                doneAt: done ? now.toISOString() : null,
                doneBy: null,
                source: done ? SOURCE_MANUAL : null,
            };
        });
        return withTrackCounts({...track, tasks});
    });
    return {...model, tracks, nextTask: nextTaskOf(tracks, model.nextTask)};
}

/** Las claves de las tareas hechas, en todas las pistas. */
export function doneTaskKeys(model) {
    if (!model) return [];
    return model.tracks.flatMap((track) => track.tasks.filter((task) => task.state === STATE_DONE)
        .map((task) => task.key));
}

/**
 * Lo que hay que celebrar: las tareas hechas que todavía no se celebraron. Sin memoria previa
 * (`celebrated === null`: primera vez de esta persona en esta agencia) no se celebra nada — lo que
 * ya estaba hecho no es algo que acaba de lograr. Tampoco lo que hizo OTRA persona («Lo hizo Ana»):
 * felicitarla por algo que no hizo se lee como un error.
 */
export function tasksToCelebrate(celebrated, model) {
    if (!celebrated || !model) return [];
    // `joined_agency` tampoco: viene hecha y no tiene un «qué se destrabó» que decir.
    return model.tracks.flatMap((track) => track.tasks
        .filter((task) => task.state === STATE_DONE && !task.doneBy && task.key !== TASK_JOINED
            && !celebrated.has(task.key))
        .map((task) => task.key));
}

/** La pista que se abre al entrar: la de la próxima tarea; si no hay, lo esencial. */
export function initialOpenTrack(model, taskKey = null) {
    if (!model || !model.tracks.length) return null;
    const wanted = (taskKey && trackOfTask(model, taskKey)) || trackOfTask(model, model.nextTask);
    return (wanted || essentialsOf(model) || model.tracks[0]).key;
}

/**
 * La tarea que se abre al entrar: la pedida por la URL (`?tarea=`, desde el aviso de un Admin) si
 * existe, si no la próxima. `joined_agency` no se abre nunca: no tiene nada adentro.
 */
export function initialOpenTask(model, requested = null) {
    if (!model) return null;
    if (requested && requested !== TASK_JOINED && taskOf(model, requested)) return requested;
    return model.nextTask;
}

/**
 * La tarea que se abre al VOLVER de una guía lanzada desde la página (C-03 del recorrido con Pablo):
 * la misma si sigue pendiente (se cerró la guía sin terminarla, o es el recorrido y falta otra guía);
 * si se hizo, la próxima pendiente de SU pista, y si la pista quedó completa, la próxima de todas.
 * La hecha queda contraída porque la página abre una sola tarea.
 */
export function taskAfterReturn(model, fromKey) {
    if (!model) return null;
    const found = fromKey ? findTask(model.tracks, fromKey) : null;
    if (!found) return model.nextTask;
    if (found.task.state === STATE_PENDING) return fromKey;
    const tasks = found.track.tasks;
    const after = tasks.slice(tasks.indexOf(found.task) + 1).find((task) => task.state === STATE_PENDING);
    return after ? after.key : model.nextTask;
}

/** ¿Hay que volver a preguntar cada 10 s? Sólo con la primera consulta abierta y pendiente (§6). */
export function needsQueryPolling(model, openTaskKey) {
    if (openTaskKey !== TASK_FIRST_QUERY) return false;
    const task = taskOf(model, TASK_FIRST_QUERY);
    return Boolean(task && task.state === STATE_PENDING);
}

/** ¿Queda algo que pasa fuera de Dashbo? Entonces volver a la pestaña puede haberlo tildado. */
export function hasOutsidePending(model) {
    return OUTSIDE_TASKS.some((key) => {
        const task = taskOf(model, key);
        return Boolean(task && task.state === STATE_PENDING);
    });
}

/** Las opciones de perfil que se ofrecen: «Liderazgo» sólo a un Admin (sus tareas no las puede hacer otro). */
export function profileChoices(isAdmin) {
    return PROFILE_ROLES.filter((role) => role !== TRACK_LEADERSHIP || isAdmin);
}

/**
 * Los chips marcados al llegar. Si la persona todavía no eligió, se preselecciona «Liderazgo» a un
 * Admin (D3) — salvo que el backend ya traiga roles (el perfil que eligió quien la invitó).
 */
export function initialProfileSelection(model) {
    if (!model) return [];
    const offered = profileChoices(model.isAdmin);
    const roles = model.profile.roles.filter((role) => offered.includes(role));
    if (model.profile.chosen || roles.length) return roles;
    return model.isAdmin ? [TRACK_LEADERSHIP] : [];
}

/** La pista es de un perfil que la persona eligió (va «· tu perfil» en el encabezado). */
export function isProfileTrack(model, trackKey) {
    return Boolean(model && model.profile.chosen && model.profile.roles.includes(trackKey));
}

/** Las pistas con su logro conseguido, para la columna derecha. */
export function achievementsOf(model) {
    return model ? model.tracks.map((track) => ({key: track.key, got: track.achieved})) : [];
}

/** La pista que sigue después de lo esencial: la primera, en el orden del payload, que no se completó. */
export function nextTrackAfterEssentials(model) {
    if (!model) return null;
    const track = model.tracks.find((each) => each.key !== TRACK_ESSENTIALS && !each.achieved
        && each.done < each.total);
    return track ? track.key : null;
}

function parseDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** Cuándo se completó lo esencial: la fecha de la última tarea que lo cerró, o `null` si no se sabe. */
export function essentialsCompletedAt(model) {
    const essentials = essentialsOf(model);
    if (!essentials || !essentials.achieved) return null;
    const dates = essentials.tasks.map((task) => parseDate(task.doneAt)).filter(Boolean);
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map((date) => date.getTime())));
}

export const LAUNCHER_PROGRESS = 'progress';
export const LAUNCHER_DONE = 'done';
export const LAUNCHER_HIDDEN = 'hidden';

/**
 * Qué dibuja el lanzador del riel (los tres estados del mockup, sección 5):
 * - `progress`: el anillo con «4/6» y la próxima tarea (el «primer día» y «a mitad de camino» son
 *   este mismo estado con otros números);
 * - `done`: «Lo esencial, completo» y la pista que sigue, durante una semana;
 * - `hidden`: pasada esa semana se retira — queda la entrada del menú, que lleva a la página con el avance.
 *
 * `firstSeenDoneAt` es cuándo ESTE navegador vio lo esencial completo por primera vez: sirve sólo si
 * el backend no trae fechas (sin ella, la semana no tendría desde cuándo contar).
 */
export function launcherView(model, {now = new Date(), firstSeenDoneAt = null} = {}) {
    const essentials = essentialsOf(model);
    if (!model || !model.enabled || !essentials) return {kind: LAUNCHER_HIDDEN};
    const base = {done: essentials.done, total: essentials.total};
    if (!essentials.achieved) return {kind: LAUNCHER_PROGRESS, ...base, nextTask: model.nextTask};
    const completedAt = essentialsCompletedAt(model) || parseDate(firstSeenDoneAt);
    if (completedAt && now.getTime() - completedAt.getTime() >= LAUNCHER_DONE_DAYS * DAY_MS) {
        return {kind: LAUNCHER_HIDDEN};
    }
    return {kind: LAUNCHER_DONE, ...base, nextTrack: nextTrackAfterEssentials(model)};
}

/**
 * `30/09`: la fecha corta de «Hecho el …», en la hora local. `null` si no hay fecha (`joined_agency`,
 * datos viejos). Se arma a mano y no con `toLocaleDateString('es', {month: '2-digit'})`: según el ICU
 * del entorno eso da «30/9». En inglés, «Sep 30».
 */
export function shortDate(value, language) {
    const date = parseDate(value);
    if (!date) return null;
    if (String(language || '').startsWith('en')) {
        return date.toLocaleDateString('en', {month: 'short', day: 'numeric'});
    }
    const pad = (number) => String(number).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

/** El porcentaje de una barra, entre 0 y 100. */
export function percentOf(done, total) {
    if (!total) return 0;
    return Math.round(Math.min(1, Math.max(0, done / total)) * 100);
}
